import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal } from '../../../../../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../../../../../../../../../../CommonComponents/OPL/Alert/Alert';
import { Button } from '../../../../../../../../../../CommonComponents/OPL/Button/Button';
import { ModalFooter } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { validation } from '../../EditAssetForm.meta';
import { EditAssetForm } from '../../EditAssetForm';
import { useBeneficiaries } from '../../../../useBeneficiaries';
import { AccountAssetDTO } from '../../../../../../api/useEstatePlanningExplanationQuery';
import { useAccountsBeneficiariesMutation } from '../../../../../../api/useAccountsBeneficiariesMutation';
import { ACCOUNT_ASSET_FORM_ID, formToBody, FormValues } from './AccountEditAssetForm.meta';
import { toast } from '../../../../../../../../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../../../../../../../../CommonConstants';

interface Props {
  asset: AccountAssetDTO;
  onRequestClose: () => void;
  isSimulation: boolean;
}

export const AccountEditAssetForm = ({ asset, onRequestClose, isSimulation }: Props) => {
  const { data } = useBeneficiaries();

  const {
    mutateAsync: updateAccountBeneficiaries,
    error: updateAccountBeneficiariesError,
  } = useAccountsBeneficiariesMutation();

  if (!data) {
    return null;
  }

  const {
    ownerDictionary,
    accountOwner,
    beneficiariesOptions: baseBeneficiariesOptions,
    trustOwner,
  } = data;

  const {
    ContingentBeneficiaries,
    PrimaryBeneficiaries,
    LastUpdated,
    Owner,
    TransferMethod,
    ...rest
  } = asset;

  const formBag = useForm<FormValues>({
    defaultValues: {
      ...rest,
      Owner,
      PrimaryBeneficiaries: PrimaryBeneficiaries || [],
      ContingentBeneficiaries: ContingentBeneficiaries || [],
      LastUpdated,
      TransferMethod: TransferMethod,
    },
    resolver: yupResolver(validation),
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    watch,
  } = formBag;

  const owner = watch('Owner');

  const beneficiariesOptions = baseBeneficiariesOptions.filter(({ label }) => {
    if (owner !== null) {
      return ownerDictionary[owner] !== label;
    }

    return accountOwner ? label !== accountOwner.label : true;
  });

  const onSubmit = handleSubmit(async values => {
    const body = formToBody(values);

    try {
      await updateAccountBeneficiaries({
        body,
        isSimulation,
      });

      onRequestClose();
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error(MESSAGES.GENERIC_ERROR);
      }
      console.error(err);
    }
  });

  const isDisabled = !!(owner && trustOwner && trustOwner.Name === owner);

  return (
    <Modal isOpen onRequestClose={onRequestClose} aria={{ labelledby: 'modal-header' }}>
      <ModalHeader as="h2" id="modal-header">
        Beneficiaries
      </ModalHeader>
      <ModalCloseButton onClick={onRequestClose} />

      <ModalBody css="padding: 0">
        <form onSubmit={onSubmit} id={ACCOUNT_ASSET_FORM_ID}>
          {updateAccountBeneficiariesError && (
            <Alert variant="error" message={updateAccountBeneficiariesError.message} m="24" />
          )}

          <FormProvider {...formBag}>
            <EditAssetForm beneficiariesOptions={beneficiariesOptions} isDisabled={isDisabled} />
          </FormProvider>
        </form>
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onRequestClose}>
          Cancel
        </Button>

        <Button type="submit" isLoading={isSubmitting} ml="8" form={ACCOUNT_ASSET_FORM_ID}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
